import {css, StyleSheet} from 'aphrodite';
import moment from 'moment';
import React from 'react';

import PrimaryFilterFormSkeleton from 'search/filters/base/PrimaryFilterFormSkeleton';
import {ButtonSize} from 'ui-library/buttons/Button';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import MenuPopover from 'ui-library/menus/MenuPopover';
import {MOMENT_DATE_FORMAT} from 'util/DateConstants';

import FlexibleDateRangePicker, {DateRange, FilterMode, FILTER_MODE} from './FlexibleDateRangePicker';

const LABEL = 'Date';

function formatDateForDisplay(isoDate: string | null, displayFormat: string): string {
  if (!isoDate) {
    return '';
  }
  const parsed = moment(isoDate, MOMENT_DATE_FORMAT, true);
  if (!parsed.isValid()) {
    return '';
  }
  const formatted = parsed.format(displayFormat);
  return displayFormat.includes('MMM') ? formatted.toUpperCase() : formatted;
}

function getDropdownToggleText(value: DateRange, mode: FilterMode, displayFormat: string): string {
  const start = formatDateForDisplay(value.startDate, displayFormat);
  const end = formatDateForDisplay(value.endDate, displayFormat);

  switch (mode) {
    case FILTER_MODE.BETWEEN:
      return start && end ? `${LABEL}: ${start} – ${end}` : LABEL;
    case FILTER_MODE.AFTER:
      return start ? `${LABEL}: After ${start}` : LABEL;
    case FILTER_MODE.BEFORE:
      return end ? `${LABEL}: Before ${end}` : LABEL;
    default:
      return LABEL;
  }
}

export interface AuditLogDateRangeFilterDropdownProps {
  value: DateRange;
  onChange: (value: DateRange) => void;
  mode: FilterMode;
  onModeChange: (mode: FilterMode) => void;
  disabled?: boolean;
  displayDateFormat?: string;
}

export default function AuditLogDateRangeFilterDropdown({
  value,
  onChange,
  mode,
  onModeChange,
  disabled = false,
  displayDateFormat = MOMENT_DATE_FORMAT,
}: AuditLogDateRangeFilterDropdownProps): JSX.Element {
  const dropdownToggleText = getDropdownToggleText(value, mode, displayDateFormat);
  const isActive = dropdownToggleText !== LABEL;

  return (
    <div>
      <PrimaryFilterFormSkeleton
        buttonSize={ButtonSize.SMALL}
        dropdownToggleText={dropdownToggleText}
        menuOverride={
          <MenuPopover className={css(styles.menuPopover)} width={MenuWidth.FIT_CONTENT}>
            <FlexibleDateRangePicker
              value={value}
              onChange={onChange}
              mode={mode}
              onModeChange={onModeChange}
              displayDateFormat={displayDateFormat}
            />
          </MenuPopover>
        }
        closeOnInsideClick={false}
        isActive={isActive}
        isDisabled={disabled}
        tooltipTitle={null}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  menuPopover: {
    padding: '16px',
  },
});
